import { useEffect, useState } from "react";
import { getGeocodeLanguageCode } from "./headerSecondNavI18n";
import { isValidCoordinates, sanitizeDisplayText } from "lib/safeDisplayText";

type GeoPermission = "undetermined" | "granted" | "denied";

export type UseHeaderSecondNavGeoParams = {
  resolvedLocale: string;
  fallbackLocationLabel: string;
};

export type UseHeaderSecondNavGeoResult = {
  /** True while waiting on GPS or the two HTTP calls */
  geoBusy: boolean;
  weatherCode: number | null;
  locationLabel: string;
  /** False when geolocation is denied, unsupported, or permanently unavailable — hide weather + location rows */
  showGeoRows: boolean;
  /** True while permission pending or APIs loading — show spinner in those rows */
  geoRowsLoading: boolean;
};

/**
 * Fetches location + weather for the second nav bar.
 *
 * 1. Ask the browser for GPS (user may deny).
 * 2. If OK: call reverse geocode + weather API in parallel.
 * 3. If GPS missing: keep `fallbackLocationLabel` and leave weather empty.
 *
 * Re-runs when `resolvedLocale` changes (e.g. language switch → new geocode language).
 */
export function useHeaderSecondNavGeo({
  resolvedLocale,
  fallbackLocationLabel,
}: UseHeaderSecondNavGeoParams): UseHeaderSecondNavGeoResult {
  const [geoBusy, setGeoBusy] = useState(true);
  const [geoPermission, setGeoPermission] = useState<GeoPermission>("undetermined");
  const [weatherCode, setWeatherCode] = useState<number | null>(null);
  const [locationLabel, setLocationLabel] = useState(() =>
    sanitizeDisplayText(fallbackLocationLabel, 120)
  );

  // CMS / prop fallback text changed
  useEffect(() => {
    setLocationLabel(sanitizeDisplayText(fallbackLocationLabel, 120));
  }, [fallbackLocationLabel]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoPermission("denied");
      setGeoBusy(false);
      setWeatherCode(null);
      return;
    }

    let stillMounted = true;
    const stop = () => {
      stillMounted = false;
    };

    setGeoPermission("undetermined");
    setGeoBusy(true);
    setWeatherCode(null);
    setLocationLabel(sanitizeDisplayText(fallbackLocationLabel, 120));

    const finishLoading = () => {
      if (stillMounted) setGeoBusy(false);
    };

    const geoLang = getGeocodeLanguageCode(resolvedLocale);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (!stillMounted) return;
        setGeoPermission("granted");
        try {
          const { latitude, longitude } = pos.coords;
          if (!isValidCoordinates(latitude, longitude)) {
            finishLoading();
            return;
          }
          const { reverseGeocodeCity, fetchWeatherCode } = await import("./secondNavGeoApis");
          const [city, code] = await Promise.all([
            reverseGeocodeCity(latitude, longitude, geoLang),
            fetchWeatherCode(latitude, longitude),
          ]);
          if (!stillMounted) return;
          if (city) setLocationLabel(city);
          if (code !== null) setWeatherCode(code);
        } finally {
          finishLoading();
        }
      },
      (err: GeolocationPositionError) => {
        if (!stillMounted) return;
        setWeatherCode(null);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoPermission("denied");
        } else {
          // Timeout / unavailable: still show the row with "—" (user did not block permission)
          setGeoPermission("granted");
        }
        finishLoading();
      },
      { enableHighAccuracy: false, timeout: 15_000, maximumAge: 300_000 }
    );

    return stop;
    // Re-fetch when locale changes (geocode language). Fallback is read on that run only.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fallback-only updates handled in a separate effect
  }, [resolvedLocale]);

  const showGeoRows = geoPermission !== "denied";
  const geoRowsLoading = showGeoRows && geoBusy;

  return { geoBusy, weatherCode, locationLabel, showGeoRows, geoRowsLoading };
}
